import React from 'react';
import Icon from '../Icon';
import IconButton from '../ui/IconButton';
import { useNotifications } from '../NotificationProvider';
import { exportScreen, ExporterKey } from './exporters';

interface ExportButtonProps {
  /** Which screen's exporter to run (an entry in exporters.ts). */
  exportKey: ExporterKey;
  /** 'icon' (default) renders the download icon alone (tooltip + accessible name "Export"); the text label returns in the mobile overflow menu. 'label' keeps the visible word. */
  variant?: 'label' | 'icon';
}

/**
 * The one Export button, used in the breadcrumb actions of every list. It is
 * icon-only by default, as Kalligo's bars are (tooltip and accessible name
 * "Export"; the word returns in the phone menu); a page can opt back into the
 * visible label with variant="label". It writes
 * whatever the screen is showing right now, as a CSV.
 */
export const ExportButton: React.FC<ExportButtonProps> = ({ exportKey, variant = 'icon' }) => {
  const { showSuccess, showInfo, showError } = useNotifications();

  const handleClick = () => {
    const outcome = exportScreen(exportKey);
    if (outcome.ok) {
      showSuccess(`Exported ${outcome.rowCount} ${outcome.rowCount === 1 ? 'row' : 'rows'} to ${outcome.fileName}`);
    } else if (outcome.reason === 'empty') {
      showInfo('Nothing to export: this list is empty.');
    } else {
      showError('This screen is still loading. Try Export again in a moment.');
    }
  };

  if (variant === 'icon') {
    return <IconButton icon="download" label="Export" title="Export this list as a CSV file" onClick={handleClick} />;
  }

  return (
    <button type="button" className="header-action-btn" onClick={handleClick} title="Export this list as a CSV file">
      <Icon name="download" size={16} />
      Export
    </button>
  );
};

export default ExportButton;
